import { useEffect, useState } from 'react';

// project imports
import api, { isDesktopClient } from 'utils/axios';

// Permite hospedar o instalador fora da API (GitHub Releases, S3, CDN...).
// Se definido, tem prioridade sobre o manifesto /desktop/latest.
const ENV_URL = (import.meta.env.VITE_APP_DESKTOP_DOWNLOAD_URL as string | undefined)?.trim();
const API_BASE = (import.meta.env.VITE_APP_API_URL as string | undefined) || 'http://localhost:22211';

export type DesktopOS = 'windows' | 'mac' | 'linux';

interface DesktopAsset {
  platform: string;
  filename: string;
  url: string;
  sizeBytes: number;
  version: string | null;
  releasedAt: string;
}

export interface DesktopDownload {
  available: boolean;
  loading: boolean;
  os: DesktopOS;
  url?: string;
  filename?: string;
  version?: string | null;
  sizeBytes?: number;
}

// Detecta o SO pelo navegador para sugerir o instalador certo.
function detectOS(): DesktopOS {
  if (typeof navigator === 'undefined') return 'windows';
  const ua = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('linux') || ua.includes('android')) return 'linux';
  return 'windows';
}

function toAbsolute(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}

// Descobre se há um app desktop empacotado disponível para download.
// Nunca retorna disponível quando já estamos rodando dentro do Electron.
export default function useDesktopDownload(): DesktopDownload {
  const os = detectOS();
  const [state, setState] = useState<DesktopDownload>({
    available: false,
    loading: !isDesktopClient(),
    os
  });

  useEffect(() => {
    // Dentro do próprio app desktop não faz sentido oferecer o download.
    if (isDesktopClient()) {
      setState({ available: false, loading: false, os });
      return;
    }

    // Override por variável de ambiente.
    if (ENV_URL) {
      setState({
        available: true,
        loading: false,
        os,
        url: ENV_URL,
        filename: ENV_URL.split('/').pop() || 'app-desktop'
      });
      return;
    }

    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/desktop/latest');
        const assets: DesktopAsset[] = data?.assets ?? [];
        if (!assets.length) {
          if (alive) setState({ available: false, loading: false, os });
          return;
        }
        const pick = assets.find((a) => a.platform === os) || assets.find((a) => a.platform === 'windows') || assets[0];
        if (alive) {
          setState({
            available: true,
            loading: false,
            os,
            // URL estável que força download como anexo (ver DesktopController).
            url: toAbsolute(`/desktop/download?platform=${pick.platform}`),
            filename: pick.filename,
            version: pick.version,
            sizeBytes: pick.sizeBytes
          });
        }
      } catch {
        // Endpoint ausente / sem instalador / offline -> simplesmente não mostra.
        if (alive) setState({ available: false, loading: false, os });
      }
    })();

    return () => {
      alive = false;
    };
    // os é derivado de forma determinística; roda uma vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
